import { BASE_API_URL } from '@http';
import { baseApi } from './baseApi';

function joinUrl(base, path) {
  const b = String(base || '').replace(/\/+$/, '');
  const p = String(path || '').replace(/^\/+/, '');
  return `${b}/${p}`;
}

const NOTIF_BASE_URL = joinUrl(BASE_API_URL, 'notif/api');

/**
 * GET /notifications/count — unread only (`is_read=false`), all types.
 * Used for: header bell badge, popover & mobile drawer “Baca Semua (N)”.
 */
export const NOTIFICATIONS_BELL_COUNT_QUERY_ARG = { is_read: false };

/** Normalize GET /notifications/count body: numeric `data` or object with totals. `count` is the primary number for this request. */
export function parseNotificationsCountEnvelope(apiResult) {
  const inner = apiResult?.data;
  const fallback = { count: 0, unread: 0, totalAll: undefined, categoryTotals: {} };

  if (inner == null || inner === '') return fallback;

  if (typeof inner === 'number' || (typeof inner === 'string' && inner !== '' && !Number.isNaN(Number(inner)))) {
    const n = Number(inner);
    return {
      count: n,
      unread: n,
      totalAll: undefined,
      categoryTotals: {},
    };
  }

  if (typeof inner !== 'object' || Array.isArray(inner)) return fallback;

  const unread = Number(inner.unread ?? inner.unread_count ?? inner.unreadCount ?? inner.unread_notifications ?? 0);

  const totalAllRaw = inner.total ?? inner.total_count ?? inner.totalCount ?? inner.all ?? inner.total_notifications;

  const totalAll =
    totalAllRaw != null && totalAllRaw !== '' && !Number.isNaN(Number(totalAllRaw)) ? Number(totalAllRaw) : undefined;

  const rawBy =
    inner.by_category ?? inner.byCategory ?? inner.categories ?? inner.category_counts ?? inner.per_category;

  const categoryTotals = {};
  const assign = (key, val) => {
    if (val == null || val === '') return;
    const n = Number(val);
    if (!Number.isNaN(n)) categoryTotals[String(key).toLowerCase()] = n;
  };

  if (rawBy && typeof rawBy === 'object' && !Array.isArray(rawBy)) {
    Object.keys(rawBy).forEach((k) => assign(k, rawBy[k]));
  }

  ['info', 'tagihan', 'transaksi', 'presensi'].forEach((k) => {
    const v = inner[k] ?? inner[`${k}_count`] ?? inner[`${k}Count`];
    assign(k, v);
  });

  const toNum = (v) => {
    if (v == null || v === '') return undefined;
    if (typeof v === 'object') return undefined;
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  };

  const count = toNum(inner.count) ?? toNum(inner.data) ?? totalAll ?? unread ?? 0;

  return { count: Number(count) || 0, unread, totalAll, categoryTotals };
}

/** Total rows from GET /notifications `pagination` meta (aligned with list response). */
export function getNotificationsPaginationTotal(pagination) {
  if (!pagination || typeof pagination !== 'object') return 0;
  const raw =
    pagination.total ??
    pagination.total_rows ??
    pagination.total_count ??
    pagination.total_items ??
    pagination.total_item ??
    pagination.total_records;
  if (raw == null || raw === '') return 0;
  const n = Number(raw);
  return Number.isNaN(n) ? 0 : n;
}

/** Ensure list endpoint `data` is an array (handles `data.items`, nested shapes). */
export function normalizeNotificationsListResponse(apiResult) {
  if (!apiResult || typeof apiResult !== 'object') return { data: [], pagination: {} };
  const raw = apiResult.data;
  let items = [];
  if (Array.isArray(raw)) {
    items = raw;
  } else if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    if (Array.isArray(raw.items)) items = raw.items;
    else if (Array.isArray(raw.notifications)) items = raw.notifications;
    else if (Array.isArray(raw.results)) items = raw.results;
    else if (Array.isArray(raw.data)) items = raw.data;
  }
  const pagination =
    apiResult.pagination ?? (raw && typeof raw === 'object' && raw.pagination ? raw.pagination : undefined) ?? {};
  return { ...apiResult, data: items, pagination };
}

export const notificationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotifications: builder.query({
      // List: `page`, `limit`, optional `notification_type` only — do not send `is_read` here (use `/notifications/count` for that).
      query: ({ page = 1, limit = 10, notification_type: notificationType } = {}) => {
        const params = { page, limit, platform: 'web-pwa' };
        if (notificationType) params.notification_type = notificationType;
        return {
          url: joinUrl(NOTIF_BASE_URL, 'notifications'),
          method: 'GET',
          params,
        };
      },
      transformResponse: (response) => normalizeNotificationsListResponse(response),
      providesTags: (result) => {
        const rows = Array.isArray(result?.data) ? result.data : [];
        if (!rows.length) return [{ type: 'Notifications', id: 'LIST' }];
        return [{ type: 'Notifications', id: 'LIST' }, ...rows.map((n) => ({ type: 'Notifications', id: n?._id }))];
      },
    }),

    getNotificationDetail: builder.query({
      query: (id) => ({
        url: joinUrl(NOTIF_BASE_URL, `notifications/${id}`),
        method: 'GET',
      }),
      providesTags: (_result, _err, id) => [{ type: 'Notifications', id }],
    }),

    getNotificationsCount: builder.query({
      query: (params = {}) => {
        const q = { platform: 'web-pwa' };
        if (params.notification_type) q.notification_type = params.notification_type;
        if (params.is_read !== undefined && params.is_read !== null) q.is_read = params.is_read;
        return {
          url: joinUrl(NOTIF_BASE_URL, 'notifications/count'),
          method: 'GET',
          params: q,
        };
      },
      transformResponse: (response) => parseNotificationsCountEnvelope(response),
      providesTags: [{ type: 'NotificationsCount', id: 'COUNT' }],
    }),

    readNotifications: builder.mutation({
      query: ({ ids }) => ({
        url: joinUrl(NOTIF_BASE_URL, 'notifications/read'),
        method: 'PUT',
        body: { ids },
      }),
      invalidatesTags: [
        { type: 'Notifications', id: 'LIST' },
        { type: 'NotificationsCount', id: 'COUNT' },
      ],
    }),

    readAllNotifications: builder.mutation({
      // Backend-supported "read all": keep ids empty, set read_all=true.
      query: () => ({
        url: joinUrl(NOTIF_BASE_URL, 'notifications/read'),
        method: 'PUT',
        body: { ids: [], read_all: true },
      }),
      invalidatesTags: [
        { type: 'Notifications', id: 'LIST' },
        { type: 'NotificationsCount', id: 'COUNT' },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetNotificationsQuery,
  useGetNotificationDetailQuery,
  useGetNotificationsCountQuery,
  useReadNotificationsMutation,
  useReadAllNotificationsMutation,
} = notificationsApi;
